import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Close as CloseIcon,
  DarkModeOutlined as DarkModeOutlinedIcon,
  LightModeOutlined as LightModeOutlinedIcon,
  MenuSharp as HamburgerIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  styled,
  SvgIcon,
  Switch,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import {
  COLOR_BLACK_PERL,
  COLOR_CERULIAN,
  COLOR_CHATHAMS_BLUE,
  COLOR_DENIM,
  COLOR_DOWN_RIVER,
  COLOR_HUMMINGBIRD,
  FONT_SIZE_45_PX,
  FONT_SIZE_49_PX,
  FONT_WEIGHT_SEMI_BOLD,
} from 'popTheme';
import LanguageToggle from 'components/landing/LanguageToggle';
import { ReactComponent as PopLogoEn } from 'components/landing/pop_logo.svg';
import { ReactComponent as PopLogoFr } from 'components/landing/pop_logo_fr.svg';
import hamburgerMenuBackgroundImage from './hamburger_menu_background.svg';
import { selectProfile } from 'redux/reducers/session';
import { sendGetProfile } from 'redux/sagas/getProfileSaga';
import { signOutAction } from 'redux/sagas/signOutSaga';
import { sendUpdateProfile } from 'redux/sagas/updateProfileSaga';

const StyledSwitch = styled(Switch)(() => ({
  width: '6.8125rem',
  height: FONT_SIZE_49_PX,
  background: 'transparent',
  '& .MuiSwitch-switchBase': {
    margin: 0,
    padding: 0,
    color: COLOR_DENIM,
    transform: 'translate(0.75rem,0.125rem)',
    '&.Mui-checked': {
      color: COLOR_CERULIAN,
      transform: 'translate(3.25rem,0.125rem)',
      '& + .MuiSwitch-track': {
        opacity: 1,
        background: COLOR_BLACK_PERL,
      },
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    background: COLOR_BLACK_PERL,
    borderRadius: FONT_SIZE_45_PX,
  },
}));

const PopAppBarContainer = ({ intl, language, navItems, mobileExtras, desktopExtras }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [openDrawer, setOpenDrawer] = useState(false);
  const location = useLocation();

  const handleDrawerOpen = () => {
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  const renderDesktopNavItems = () =>
    navItems.map((item) => (
      <Button component={Link} key={item.link} to={item.link}>
        <Typography
          variant="h6"
          sx={{ color: 'text.secondary', mr: '3rem', fontWeight: FONT_WEIGHT_SEMI_BOLD }}
        >
          {item.display}
        </Typography>
      </Button>
    ));

  const renderMobileNavItems = () =>
    navItems.map((item) => (
      <Grid item key={item.link}>
        <Button component={Link} key={item.link} to={item.link} onClick={handleDrawerClose}>
          <Typography variant="h2" color={theme.palette.title.main}>
            {item.display}
          </Typography>
        </Button>
      </Grid>
    ));

  return (
    <AppBar component="nav" position="static" elevation={0} color="transparent">
      <Toolbar variant="dense" disableGutters>
        <Box sx={{ flexGrow: 1, display: { xs: 'block' } }}>
          <IconButton
            disableRipple
            onClick={() => {
              navigate('');
            }}
            data-testid="pop-logo-button"
          >
            <SvgIcon
              component={language === 'FRENCH' || intl.locale === 'fr' ? PopLogoFr : PopLogoEn}
              inheritViewBox
              sx={{
                height: { xs: '3rem', sm: '2.25rem', md: '3.75rem', lg: '5.5rem' },
                width: { xs: '6rem', sm: '4.5rem', md: '7.5rem', lg: '11.25rem' },
              }}
            />
          </IconButton>
        </Box>
        <Box sx={{ alignItems: 'end', flexGrow: 0, display: { xs: 'none', sm: 'block' } }}>
          {renderDesktopNavItems()}
          {desktopExtras}
        </Box>
        <Box sx={{ alignItems: 'end', flexGrow: 0, display: { xs: 'block', sm: 'none' } }}>
          <Grid container direction="row">
            {location.pathname.includes('/passwordreset') && (
              <Grid item>
                <LanguageToggle />
              </Grid>
            )}
            <Grid item>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleDrawerOpen}
                data-testid="hamburger-menu-open"
              >
                <HamburgerIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Toolbar>
      <Drawer
        anchor="right"
        open={openDrawer}
        PaperProps={{
          sx: {
            width: '100%',
            backgroundImage: `url(${hamburgerMenuBackgroundImage})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundColor: COLOR_CHATHAMS_BLUE,
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end" marginRight="3rem" marginTop="3rem">
          <IconButton
            edge="end"
            color="inherit"
            aria-label="close"
            onClick={handleDrawerClose}
            data-testid="hamburger-menu-close"
          >
            <CloseIcon fontSize="large" sx={{ color: theme.palette.title.main }} />
          </IconButton>
        </Box>
        <Grid
          container
          direction="column"
          wrap="nowrap"
          justifyContent="center"
          alignItems="center"
          rowSpacing="3rem"
          sx={{ height: '100%' }}
        >
          {renderMobileNavItems()}
          {mobileExtras}
        </Grid>
      </Drawer>
    </AppBar>
  );
};

export const PopAppBar = () => {
  const currentProfile = useSelector(selectProfile);
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useTheme();

  useEffect(() => {
    dispatch(sendGetProfile());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logOut = () => dispatch(signOutAction());

  const handleToggleMode = () => {
    const valueToStore = currentProfile?.uiMode === 'LIGHT' ? 'DARK' : 'LIGHT';
    dispatch(sendUpdateProfile({ ...currentProfile, uiMode: valueToStore }));
  };

  const messages = defineMessages({
    appBarLinkCheckIn: {
      id: 'appbar.link.checkin',
      defaultMessage: 'Check In',
      description: 'Link text for app bar check in',
    },
    appBarLinkTrends: {
      id: 'appbar.link.results',
      defaultMessage: 'Results',
      description: 'Link text for app bar results',
    },
    appBarLinkSettings: {
      id: 'appbar.link.settings',
      defaultMessage: 'Settings',
      description: 'Link text for app bar settings',
    },
    appBarLinkHome: {
      id: 'appbar.link.home',
      defaultMessage: 'Home',
      description: 'Link text for app bar home',
    },
    appBarLinkLogOut: {
      id: 'appbar.link.logout',
      defaultMessage: 'Log Out',
      description: 'Link text for app bar logout',
    },
  });

  const navItems = [
    { display: intl.formatMessage(messages.appBarLinkHome), link: '' },
    { display: intl.formatMessage(messages.appBarLinkCheckIn), link: 'checkin' },
    { display: intl.formatMessage(messages.appBarLinkTrends), link: 'results' },
    { display: intl.formatMessage(messages.appBarLinkSettings), link: 'settings' },
  ];

  return (
    <PopAppBarContainer
      intl={intl}
      language={currentProfile?.language}
      navItems={navItems}
      mobileExtras={[
        <Grid item key="uiModeToggle">
          <StyledSwitch
            checked={currentProfile?.uiMode === 'DARK'}
            onChange={handleToggleMode}
            icon={
              <LightModeOutlinedIcon
                sx={{
                  background: COLOR_HUMMINGBIRD,
                  borderRadius: '50%',
                  width: FONT_SIZE_45_PX,
                  height: FONT_SIZE_45_PX,
                }}
              />
            }
            checkedIcon={
              <DarkModeOutlinedIcon
                sx={{
                  background: COLOR_DOWN_RIVER,
                  borderRadius: '50%',
                  width: FONT_SIZE_45_PX,
                  height: FONT_SIZE_45_PX,
                }}
              />
            }
          />
        </Grid>,
        <Grid item key="logout">
          <Button onClick={logOut}>
            <Typography variant="h2" color={theme.palette.title.main}>
              {intl.formatMessage(messages.appBarLinkLogOut)}
            </Typography>
          </Button>
        </Grid>,
      ]}
      desktopExtras={[
        <IconButton
          disableRipple
          onClick={handleToggleMode}
          key="uiModeToggle"
          data-testid="ui-mode-toggle-button"
          sx={{ color: theme.palette.primary.main, mr: '3rem' }}
        >
          {currentProfile?.uiMode === 'DARK' ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
        </IconButton>,
        <Button
          variant="contained"
          disableElevation
          disableRipple
          onClick={logOut}
          key="logout"
          sx={{ backgroundColor: COLOR_HUMMINGBIRD, height: '2.5rem' }}
        >
          <Typography variant="h6" sx={{ color: COLOR_DENIM }}>
            {intl.formatMessage(messages.appBarLinkLogOut)}
          </Typography>
        </Button>,
      ]}
      messages={messages}
    />
  );
};

export const PopAppBarLoginOnly = () => {
  const currentProfile = useSelector(selectProfile);
  const intl = useIntl();
  const messages = defineMessages({
    appBarLinkLogIn: {
      id: 'appbar.link.login',
      defaultMessage: 'Log In',
      description: 'Link text for app bar logout',
    },
  });

  return (
    <PopAppBarContainer
      intl={intl}
      language={currentProfile?.language}
      navItems={[]}
      desktopExtras={[
        <LanguageToggle key="languageToggle" />,
        <Button
          variant="contained"
          disableElevation
          disableRipple
          component={Link}
          key="login"
          to={'/'}
          sx={{
            backgroundColor: COLOR_HUMMINGBIRD,
            color: COLOR_DENIM,
            height: '2.5rem',
            ml: '2.5rem',
          }}
        >
          <Typography variant="h6" sx={{ color: COLOR_DENIM }}>
            {intl.formatMessage(messages.appBarLinkLogIn)}
          </Typography>
        </Button>,
      ]}
      mobileExtras={[
        <Grid item key="languageToggle">
          <LanguageToggle />
        </Grid>,
        <Grid item key="login">
          <Button component={Link} to={'/'}>
            <Typography variant="h2">{intl.formatMessage(messages.appBarLinkLogIn)}</Typography>
          </Button>
        </Grid>,
      ]}
      messages={messages}
    />
  );
};
